import React, { useCallback, useEffect, useState } from 'react';
import { FileCheck2, Loader2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { fmtDT } from '@/components/ewayBill/shared';
import { blobErr, jwTransferApi } from '@/services/jwTransferApi';

/** {ref: e-way bill status} for job order numbers (kind 'job_order') or Processing transfer ids (kind 'processing'). */
export const useJwEwb = (kind, refs) => {
  const key = (refs || []).filter(Boolean).join('|');
  const [map, setMap] = useState({});
  const load = useCallback(() => {
    if (!key) { setMap({}); return; }
    jwTransferApi.status(kind, key.split('|')).then(setMap).catch(() => setMap({}));
  }, [kind, key]);
  useEffect(() => { load(); }, [load]);
  return [map, load];
};

export const JwEwbCell = ({ s, refNo, testId }) => {
  if (!s) return <span className="text-xs text-gray-400" data-testid={testId}>…</span>;
  return (
    <div className="leading-tight" data-testid={testId}>
      {s.ewb_no ? <span className="font-mono text-xs font-semibold text-emerald-800">{s.ewb_no}</span>
        : <span className={`text-xs ${s.status ? 'text-amber-700' : 'text-gray-500'}`}>{s.status_label}</span>}
      {s.ewb_no && <div className={`text-[10px] ${s.expired ? 'text-rose-700' : 'text-gray-500'}`}>{s.expired ? 'Expired' : s.valid_upto ? `till ${fmtDT(s.valid_upto)}` : s.status_label}</div>}
      {refNo && s.doc_no !== refNo && <div className="text-[10px] font-mono text-gray-400">doc {s.doc_no}</div>}
    </div>
  );
};

export const JwTransferPrintBtn = ({ kind, refNo, s, testId, label = 'Job Work Transfer', className = '' }) => {
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.stopPropagation();
    const win = window.open('', '_blank');
    setBusy(true);
    try {
      const url = URL.createObjectURL(await jwTransferApi.pdf(kind, refNo));
      if (win && !win.closed) win.location.href = url;
      else Object.assign(document.createElement('a'), { href: url, download: `job_work_transfer_${refNo}.pdf` }).click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      win?.close();
      toast.error(await blobErr(err, 'Could not make the Job Work Transfer'));
    } finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" className={`h-8 ${className}`} onClick={run} disabled={busy || !s?.ewb_no} data-testid={testId}
      title={s?.ewb_no ? 'Print the Job Work Transfer' : 'Prints once the e-way bill number is filled'}>
      {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Printer className="w-3.5 h-3.5 mr-1" />}{label}
    </Button>
  );
};

/** Job order detail pages: its e-way bill (doc no + EWB No / status) and the Job Work Transfer print. */
export const JwEwbBar = ({ kind = 'job_order', refNo, testPrefix = 'jw-ewb' }) => {
  const [map] = useJwEwb(kind, [refNo]);
  const s = map[refNo];
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#E5DFD0] bg-white px-4 py-3" data-testid={`${testPrefix}-bar`}>
      <FileCheck2 className="w-5 h-5 text-[#2D5F4F]" />
      <div>
        <div className="text-[11px] uppercase tracking-wide text-gray-500">E-way bill{s ? <span className="normal-case font-mono"> · doc {s.doc_no}</span> : null}</div>
        <JwEwbCell s={s} testId={`${testPrefix}-status`} />
      </div>
      <JwTransferPrintBtn kind={kind} refNo={refNo} s={s} testId={`${testPrefix}-print-btn`} className="ml-auto" />
    </div>
  );
};
